import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import { UrlApi } from "../../../../services/apirest";

const ALL_FIELDS = [
  { field_name: "cuit" },
  { field_name: "fecha_emision" },
  { field_name: "fecha_vigencia_desde" },
  { field_name: "fecha_vigencia_hasta" },
  { field_name: "tipo_contr_insc" },
  { field_name: "percepcion" },
  { field_name: "retencion" },
  { field_name: "denominacion" },
  { field_name: "valor" },
];

const COL_TYPES = [
  { value: "", label: "Ninguno (texto plano)" },
  { value: "date_ddmmyyyy", label: "date_ddmmyyyy" },
  { value: "decimal_arg", label: "decimal_arg" },
];

const FORMAT_TYPES = [
  { value: "ARCHIVO_COMBINADO", label: "ARCHIVO_COMBINADO — un solo archivo (CABA, SF, ER, Jujuy...)" },
  { value: "ARCHIVOS_SEPARADOS", label: "ARCHIVOS_SEPARADOS — percepciones + retenciones (ARBA, Córdoba)" },
];

const REQUIRED_FIELDS = {
  ARCHIVO_COMBINADO: ["percepcion", "retencion"],
  ARCHIVOS_SEPARADOS: ["denominacion", "valor"],
};

function getRequiredFields(format_type) {
  return REQUIRED_FIELDS[format_type] ?? [];
}

function isFieldMissing(f) {
  return f.col_index === "" || f.col_index === null || f.col_index === undefined;
}

function validateRequiredFields(fields, format_type) {
  const required = getRequiredFields(format_type);
  return required.filter((name) => {
    const f = fields.find((fi) => fi.field_name === name);
    return !f || isFieldMissing(f);
  });
}

const emptyFields = () =>
  ALL_FIELDS.map((f) => ({
    field_name: f.field_name,
    col_index: "",
    col_type: "",
    default_val: "",
  }));

function mergeFields(serverFields) {
  return ALL_FIELDS.map((f) => {
    const existing = serverFields?.find((sf) => sf.field_name === f.field_name);
    return {
      field_name: f.field_name,
      col_index: existing?.col_index ?? "",
      col_type: existing?.col_type ?? "",
      default_val: existing?.default_val ?? "",
    };
  });
}

const emptyNewProv = () => ({
  id_provincia: "",
  provincia: "",
  separator: ";",
  min_columns: "",
  format_type: "ARCHIVO_COMBINADO",
  fields: emptyFields(),
});

function ConfiguracionPadrones({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState([]);
  const [todasProvincias, setTodasProvincias] = useState([]);
  const [tabIdx, setTabIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newProv, setNewProv] = useState(emptyNewProv());

  const loadSchemas = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${UrlApi}/padron-schemas`),
      axios.get(`${UrlApi}/provincias`),
    ])
      .then(([schemasRes, provinciasRes]) => {
        setEditData(
          schemasRes.data.map((s) => ({
            ...s,
            separator: s.separator ?? ";",
            min_columns: s.min_columns ?? "",
            format_type: s.format_type ?? "ARCHIVO_COMBINADO",
            fields: mergeFields(s.fields),
          })),
        );
        setTodasProvincias(provinciasRes.data);
        setTabIdx(0);
      })
      .catch(() => setError("Error al cargar la configuración"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) loadSchemas();
  }, [open, loadSchemas]);

  const updateField = (provIdx, fieldName, key, value) => {
    setEditData((prev) =>
      prev.map((p, pi) =>
        pi !== provIdx
          ? p
          : {
              ...p,
              fields: p.fields.map((f) =>
                f.field_name === fieldName ? { ...f, [key]: value } : f,
              ),
            },
      ),
    );
  };

  const updateProvMeta = (provIdx, key, value) => {
    setEditData((prev) =>
      prev.map((p, pi) => (pi === provIdx ? { ...p, [key]: value } : p)),
    );
  };

  const buildBody = (prov) => ({
    separator: prov.separator,
    format_type: prov.format_type || "ARCHIVO_COMBINADO",
    min_columns: prov.min_columns !== "" ? Number(prov.min_columns) : null,
    fields: prov.fields.map((f) => ({
      field_name: f.field_name,
      col_index:
        f.col_index !== "" && f.col_index !== null ? Number(f.col_index) : null,
      col_type: f.col_type || null,
      default_val: f.default_val !== "" ? f.default_val : null,
    })),
  });

  const handleSave = async () => {
    const prov = editData[tabIdx];
    if (!prov) return;
    const missing = validateRequiredFields(prov.fields, prov.format_type);
    if (missing.length > 0) {
      setError(`Campos requeridos sin índice: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.put(
        `${UrlApi}/padron-schemas/${prov.id_provincia}`,
        buildBody(prov),
      );
      setSuccess("Configuración guardada correctamente");
    } catch {
      setError("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProv = async () => {
    const missing = validateRequiredFields(newProv.fields, newProv.format_type);
    if (missing.length > 0) {
      setError(`Campos requeridos sin índice: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        id_provincia: newProv.id_provincia,
        ...buildBody(newProv),
      };
      await axios.post(`${UrlApi}/padron-schemas`, body);
      await axios.put(`${UrlApi}/provincias/${newProv.id_provincia}`, {
        iibb_auto: 1,
      });
      setSuccess("Provincia agregada al padrón correctamente");
      setShowNew(false);
      setNewProv(emptyNewProv());
      loadSchemas();
    } catch {
      setError("Error al agregar la provincia al padrón");
    } finally {
      setSaving(false);
    }
  };

  const currentProv = editData[tabIdx];

  const provinciaIdsEnPadron = new Set(editData.map((e) => e.id_provincia));
  const provinciasDisponibles = todasProvincias.filter(
    (p) => !provinciaIdsEnPadron.has(p.id),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1F618D 0%, #2980B9 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 2,
        }}
      >
        <SettingsIcon />
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1}>
            Configuración de Padrones
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Mapeo de columnas por provincia
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", minHeight: 460 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              py: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Sidebar */}
            <Box
              sx={{
                width: 190,
                borderRight: "1px solid #e0e0e0",
                bgcolor: "#F8FBFD",
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Tabs
                orientation="vertical"
                value={showNew ? false : tabIdx}
                onChange={(_, v) => {
                  setTabIdx(v);
                  setShowNew(false);
                  setError(null);
                  setSuccess(null);
                }}
                sx={{
                  flex: 1,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    alignItems: "flex-start",
                    px: 2,
                    minHeight: 40,
                    fontSize: "0.85rem",
                  },
                }}
              >
                {editData.map((p, i) => (
                  <Tab key={p.id_provincia} label={p.provincia} value={i} />
                ))}
              </Tabs>
              <Divider />
              <Button
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => {
                  setShowNew(true);
                  setError(null);
                  setSuccess(null);
                }}
                sx={{
                  textTransform: "none",
                  color: "#1F618D",
                  fontWeight: 600,
                  py: 1.2,
                  borderRadius: 0,
                }}
              >
                Nueva Provincia
              </Button>
            </Box>

            {/* Panel principal */}
            <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
              {(error || success) && (
                <Alert
                  severity={error ? "error" : "success"}
                  sx={{ mb: 2 }}
                  onClose={() => {
                    setError(null);
                    setSuccess(null);
                  }}
                >
                  {error || success}
                </Alert>
              )}

              {showNew ? (
                <NewProvinciaForm
                  data={newProv}
                  provinciasDisponibles={provinciasDisponibles}
                  onSelectProvincia={(prov) =>
                    setNewProv((prev) => ({
                      ...prev,
                      id_provincia: prov.id,
                      provincia: prov.provincia,
                    }))
                  }
                  onChangeMeta={(k, v) => setNewProv((p) => ({ ...p, [k]: v }))}
                  onChangeField={(fieldName, key, val) =>
                    setNewProv((prev) => ({
                      ...prev,
                      fields: prev.fields.map((f) =>
                        f.field_name === fieldName ? { ...f, [key]: val } : f,
                      ),
                    }))
                  }
                  onSave={handleCreateProv}
                  saving={saving}
                />
              ) : currentProv ? (
                <ProvinciaEditor
                  prov={currentProv}
                  onChangeMeta={(k, v) => updateProvMeta(tabIdx, k, v)}
                  onChangeField={(fn, key, val) =>
                    updateField(tabIdx, fn, key, val)
                  }
                />
              ) : null}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #eee" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="error"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cerrar
        </Button>
        {!showNew && currentProv && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              backgroundColor: "#1F618D",
              "&:hover": { backgroundColor: "#154360" },
            }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ProvinciaEditor({ prov, onChangeMeta, onChangeField }) {
  return (
    <>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          label="Separador"
          size="small"
          value={prov.separator}
          onChange={(e) => onChangeMeta("separator", e.target.value)}
          sx={{ width: 110 }}
        />
        <TextField
          label="Mín. columnas"
          type="number"
          size="small"
          value={prov.min_columns}
          onChange={(e) => onChangeMeta("min_columns", e.target.value)}
          sx={{ width: 140 }}
          inputProps={{ min: 0 }}
        />
        <FormControl size="small" sx={{ width: 340 }}>
          <InputLabel>Tipo de formato</InputLabel>
          <Select
            label="Tipo de formato"
            value={prov.format_type ?? "ARCHIVO_COMBINADO"}
            onChange={(e) => onChangeMeta("format_type", e.target.value)}
          >
            {FORMAT_TYPES.map((ft) => (
              <MenuItem key={ft.value} value={ft.value}>
                {ft.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FieldsTable fields={prov.fields} format_type={prov.format_type} onChangeField={onChangeField} />
    </>
  );
}

function NewProvinciaForm({
  data,
  provinciasDisponibles,
  onSelectProvincia,
  onChangeMeta,
  onChangeField,
  onSave,
  saving,
}) {
  return (
    <>
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        Agregar Provincia al Padrón
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ width: 230 }}>
          <InputLabel>Provincia</InputLabel>
          <Select
            label="Provincia"
            value={data.id_provincia}
            onChange={(e) => {
              const selected = provinciasDisponibles.find(
                (p) => p.id === e.target.value,
              );
              if (selected) onSelectProvincia(selected);
            }}
          >
            {provinciasDisponibles.length === 0 && (
              <MenuItem disabled value="">
                No hay provincias disponibles
              </MenuItem>
            )}
            {provinciasDisponibles.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.provincia}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Separador"
          size="small"
          value={data.separator}
          onChange={(e) => onChangeMeta("separator", e.target.value)}
          sx={{ width: 100 }}
        />
        <TextField
          label="Mín. columnas"
          type="number"
          size="small"
          value={data.min_columns}
          onChange={(e) => onChangeMeta("min_columns", e.target.value)}
          sx={{ width: 140 }}
          inputProps={{ min: 0 }}
        />
        <FormControl size="small" sx={{ width: 340 }}>
          <InputLabel>Tipo de formato</InputLabel>
          <Select
            label="Tipo de formato"
            value={data.format_type ?? "ARCHIVO_COMBINADO"}
            onChange={(e) => onChangeMeta("format_type", e.target.value)}
          >
            {FORMAT_TYPES.map((ft) => (
              <MenuItem key={ft.value} value={ft.value}>
                {ft.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FieldsTable fields={data.fields} format_type={data.format_type} onChangeField={onChangeField} />
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          variant="contained"
          disabled={saving || !data.id_provincia}
          onClick={onSave}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            backgroundColor: "#1F618D",
            "&:hover": { backgroundColor: "#154360" },
          }}
        >
          {saving ? "Guardando..." : "Agregar al Padrón"}
        </Button>
      </Box>
    </>
  );
}

function FieldsTable({ fields, format_type, onChangeField }) {
  const requiredFields = getRequiredFields(format_type);
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "#EAF4FB" }}>
          <TableCell sx={{ fontWeight: 700 }}>Campo</TableCell>
          <TableCell sx={{ fontWeight: 700, width: 110 }}>Índice col.</TableCell>
          <TableCell sx={{ fontWeight: 700, width: 240 }}>Tipo</TableCell>
          <TableCell sx={{ fontWeight: 700, width: 120 }}>Default</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map((f) => {
          const hasIndex =
            f.col_index !== "" &&
            f.col_index !== null &&
            f.col_index !== undefined;
          const isRequired = requiredFields.includes(f.field_name);
          // percepcion/retencion no aplican en ARCHIVOS_SEPARADOS; denominacion/valor no aplican en ARCHIVO_COMBINADO
          const isNotApplicable =
            (format_type === "ARCHIVOS_SEPARADOS" && (f.field_name === "percepcion" || f.field_name === "retencion")) ||
            (format_type === "ARCHIVO_COMBINADO" && (f.field_name === "denominacion" || f.field_name === "valor"));
          const isMissing = isRequired && isFieldMissing(f);
          return (
            <TableRow
              key={f.field_name}
              sx={{
                "&:last-child td": { border: 0 },
                ...(isNotApplicable && { opacity: 0.35 }),
                ...(isMissing && { backgroundColor: "#FFF3F3" }),
              }}
            >
              <TableCell>
                <Typography variant="body2" fontFamily="monospace">
                  {f.field_name}
                  {isRequired && (
                    <Typography component="span" color="error" fontSize="0.85rem" ml={0.5}>*</Typography>
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <TextField
                  type="number"
                  size="small"
                  value={f.col_index ?? ""}
                  onChange={(e) =>
                    onChangeField(f.field_name, "col_index", e.target.value)
                  }
                  inputProps={{ min: 0 }}
                  sx={{ width: 90 }}
                  placeholder="—"
                  disabled={isNotApplicable}
                />
              </TableCell>
              <TableCell>
                <Select
                  size="small"
                  value={f.col_type ?? ""}
                  onChange={(e) =>
                    onChangeField(f.field_name, "col_type", e.target.value)
                  }
                  sx={{ width: 220 }}
                  disabled={isNotApplicable}
                >
                  {COL_TYPES.map((ct) => (
                    <MenuItem key={ct.value} value={ct.value}>
                      {ct.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={f.default_val ?? ""}
                  onChange={(e) =>
                    onChangeField(f.field_name, "default_val", e.target.value)
                  }
                  disabled={hasIndex || isNotApplicable}
                  placeholder={hasIndex ? "—" : "default"}
                  sx={{ width: 100 }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export default ConfiguracionPadrones;
